import {
  TypeCardsRowFields,
  TypeProductCardFields,
} from '@/core/generated-types';
import React, { FC, memo } from 'react';
import { InternalLink, LuvImage } from '@/components';
import classNames from 'classnames';

interface RelatedProductsCardInt extends TypeProductCardFields {
  classes?: string;
  isPlayKits: boolean;
  isSingularCard: boolean;
}

const RelatedProductsCard: FC<RelatedProductsCardInt> = ({
  entryName,
  product,
  productLink,
  featuredImageOverride,
  classes,
  isPlayKits,
  isSingularCard,
}) => {
  const { listingImage, title, ageRange } = product.fields || {};
  const featuredImage = featuredImageOverride || listingImage;

  const imageClasses = isPlayKits
    ? 'h-auto w-full object-cover'
    : 'h-auto w-[360px] object-cover md:h-[360px]';

  return (
    <InternalLink link={productLink} classes={classes} hideArrow>
      <div
        className={classNames(isSingularCard ? 'mx-auto md:max-w-[504px]' : '')}
        data-testid={`product-card-${entryName}`}
      >
        <LuvImage
          height={360}
          width={isPlayKits ? 480 : 360}
          imageData={featuredImage}
          alt={featuredImage?.fields?.altText}
          className={imageClasses}
        />
        <p className="mt-2 text-[18px] font-semibold md:text-h2">{title}</p>
        {ageRange && <p className="text-base md:text-h3">{ageRange}</p>}
      </div>
    </InternalLink>
  );
};

const CardsRow: FC<TypeCardsRowFields> = ({
  id,
  title,
  cards,
  isPlayKits,
  backgroundColor,
}) => {
  const isSingularCard = cards?.length === 1;
  const gridClasses =
    cards?.length == 2
      ? `grid ${
          isPlayKits ? 'grid-cols-2' : 'grid-cols-1'
        } items-start md:grid-cols-2 gap-2 md:gap-6`
      : 'flex flex-col items-start md:flex-row md:gap-4';

  return (
    <div
      id={id}
      className={`mx-auto px-6 py-10 md:px-12 md:py-24 bg-${backgroundColor?.fields?.color}`}
      data-testid="cards-row"
    >
      <div className="max-w-[1128px] mx-auto">
        <div className="mx-auto mb-6 flex flex-col gap-4 text-center md:mb-10">
          <h2 className="text-[24px] font-bold md:text-[32px]">{title}</h2>
        </div>
        <div className={`${gridClasses}`} data-testid="cards-row-grid">
          {cards?.map((card, index) => {
            const { sys, fields } = card;

            let classes = '';
            if (cards.length == 2) {
              classes = !index
                ? 'justify-center md:justify-end'
                : 'justify-center md:justify-start';
            } else {
              classes = 'mx-auto mb-8 md:mb-0';
            }

            return (
              <RelatedProductsCard
                key={sys.id}
                {...fields}
                classes={classes}
                isPlayKits={isPlayKits}
                isSingularCard={isSingularCard}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default memo(CardsRow);
